import { useEffect, useState, useCallback, useMemo } from "react";
import {
  X,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  UserPlus,
  Trash2,
  Edit2,
  ArrowLeft,
  ShoppingCart,
  Plus,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  ExternalLink,
  ChevronRight,
  Package,
  FileSpreadsheet,
  IndianRupee,
  Tag,
  User,
  Info,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { money, testid, getTodayDateString } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone, canDeleteLead } from "../../lib/permissions";
import RequestPriceModal from "./RequestPriceModal";
import AddLeadOrderModal from "./AddLeadOrderModal";
import BulkLeadOrderUploadModal from "./BulkLeadOrderUploadModal";
import UpdateLeadOrderPriceModal from "./UpdateLeadOrderPriceModal";
import { EditLeadModal } from "./Leads";

const STATUSES = [
  "New",
  "Not Answered",
  "Not Interested",
  "Contacted",
  "Interested",
  "Qualified",
  "Proposal Sent",
  "Negotiation",
  "Won",
  "Lost",
];

const STATUS_PILL_STYLES = {
  New: { activeBg: "#eff6ff", activeBorder: "#3b82f6", activeText: "#1d4ed8", dot: "#3b82f6" },
  "Not Answered": { activeBg: "#fffbeb", activeBorder: "#f59e0b", activeText: "#b45309", dot: "#f59e0b" },
  "Not Interested": { activeBg: "#fef2f2", activeBorder: "#ef4444", activeText: "#b91c1c", dot: "#ef4444" },
  Contacted: { activeBg: "#eff6ff", activeBorder: "#3b82f6", activeText: "#1d4ed8", dot: "#3b82f6" },
  Interested: { activeBg: "#f0fdf4", activeBorder: "#22c55e", activeText: "#15803d", dot: "#22c55e" },
  Qualified: { activeBg: "#fef3c7", activeBorder: "#f59e0b", activeText: "#92400e", dot: "#f59e0b" },
  "Proposal Sent": { activeBg: "#fef3c7", activeBorder: "#f59e0b", activeText: "#92400e", dot: "#f59e0b" },
  Negotiation: { activeBg: "#fef3c7", activeBorder: "#f59e0b", activeText: "#92400e", dot: "#f59e0b" },
  Won: { activeBg: "#ecfdf5", activeBorder: "#10b981", activeText: "#047857", dot: "#10b981" },
  Lost: { activeBg: "#fef2f2", activeBorder: "#ef4444", activeText: "#b91c1c", dot: "#ef4444" },
};

const PROC_STATUS_BADGES = {
  Pending: { bg: "#fef3c7", text: "#92400e", border: "#fde68a" },
  "Partially Priced": { bg: "#f3e8ff", text: "#7e22ce", border: "#e9d5ff" },
  "In Progress": { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd" },
  "Price Received": { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" },
  Completed: { bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" },
};

const PROC_PRIORITY_BADGES = {
  Urgent: { bg: "#fee2e2", text: "#b91c1c" },
  High: { bg: "#ffedd5", text: "#c2410c" },
  Medium: { bg: "#f1f5f9", text: "#475569" },
  Low: { bg: "#f8fafc", text: "#64748b" },
};

function ActivityIcon({ type }) {
  const map = {
    call: Phone,
    email: Mail,
    meeting: Calendar,
    note: MessageSquare,
    status_change: ChevronRight,
    convert: UserPlus,
  };
  const Icon = map[type] || MessageSquare;
  return (
    <div
      style={{
        width: 26,
        height: 26,
        borderRadius: 6,
        background: "#f1f5f9",
        border: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#475569",
        flexShrink: 0,
      }}
    >
      <Icon size={13} />
    </div>
  );
}

function formatCurrency(amount, currency = "INR") {
  if (amount === null || amount === undefined || isNaN(amount)) return "—";
  const symbol = currency === "INR" ? "₹" : currency === "USD" ? "$" : currency === "EUR" ? "€" : `${currency} `;
  return `${symbol}${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export default function LeadDetail({ leadId, user, onBack, onOpenOrder, onEdit }) {
  const [lead, setLead] = useState(null);
  const [priceRequests, setPriceRequests] = useState([]);
  const [loadingProc, setLoadingProc] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [showCompletedProc, setShowCompletedProc] = useState(false);
  const [selectedReqDetail, setSelectedReqDetail] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Lead Orders state
  const [leadOrders, setLeadOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showAddSingleOrder, setShowAddSingleOrder] = useState(false);
  const [showUploadBulkOrders, setShowUploadBulkOrders] = useState(false);
  const [showAddOrderMenu, setShowAddOrderMenu] = useState(false);
  const [priceOrder, setPriceOrder] = useState(null);
  const [selectedLeadOrderIds, setSelectedLeadOrderIds] = useState([]);
  const [ordersForPriceRequest, setOrdersForPriceRequest] = useState([]);

  const [busy, setBusy] = useState("");
  const [activity, setActivity] = useState({ type: "call", summary: "", outcome: "" });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [followUp, setFollowUp] = useState({ next_follow_up: "", notes: "" });

  const canEdit = isAdmin(user) || canAccessDepartment(user, ["Sales"]);
  const canDelete = canDeleteLead(user);

  const allOrdersSelected = leadOrders.length > 0 && selectedLeadOrderIds.length === leadOrders.length;
  const isOrdersIndeterminate = selectedLeadOrderIds.length > 0 && selectedLeadOrderIds.length < leadOrders.length;

  const toggleSelectAllOrders = () => {
    if (allOrdersSelected) {
      setSelectedLeadOrderIds([]);
    } else {
      setSelectedLeadOrderIds(leadOrders.map((lo) => lo.order_id));
    }
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedLeadOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const handleRequestPriceForSelected = () => {
    const selected = leadOrders.filter((lo) => selectedLeadOrderIds.includes(lo.order_id));
    if (selected.length === 0) {
      toast.error("Please select at least one product to request pricing.");
      return;
    }
    setOrdersForPriceRequest(selected);
    setPriceModalOpen(true);
  };

  const handleOpenGeneralPriceModal = () => {
    if (selectedLeadOrderIds.length > 0) {
      const selected = leadOrders.filter((lo) => selectedLeadOrderIds.includes(lo.order_id));
      setOrdersForPriceRequest(selected);
    } else {
      setOrdersForPriceRequest([]);
    }
    setPriceModalOpen(true);
  };

  const loadLead = useCallback(async () => {
    try {
      const r = await api("get", `/leads/${leadId}`);
      setLead(r.data);
    } catch (e) {
      toast.error("Could not load lead details");
      if (onBack && (e.response?.status === 404 || e.response?.status === 403)) {
        onBack();
      }
    }
  }, [leadId, onBack]);

  const loadLeadOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const res = await api("get", `/leads/${leadId}/orders`);
      setLeadOrders(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // noop
    } finally {
      setLoadingOrders(false);
    }
  }, [leadId]);

  const loadProcurementRequests = useCallback(async () => {
    setLoadingProc(true);
    try {
      const res = await api("get", `/procurement?lead_id=${encodeURIComponent(leadId)}`);
      setPriceRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // noop
    } finally {
      setLoadingProc(false);
    }
  }, [leadId]);

  useEffect(() => {
    loadLead();
    loadProcurementRequests();
    loadLeadOrders();
  }, [loadLead, loadProcurementRequests, loadLeadOrders]);

  const handleDeleteLeadOrder = async (orderId) => {
    try {
      await api("delete", `/leads/${leadId}/orders/${orderId}`);
      toast.success("Lead order removed");
      loadLeadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove order");
    }
  };

  const changeStatus = async (status) => {
    if (!status || status === lead?.status) return;
    setBusy("status");
    try {
      await api("patch", `/leads/${leadId}`, { status });
      toast.success(`Moved status to ${status}`);
      await loadLead();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
    } finally {
      setBusy("");
    }
  };

  const handleConvert = async () => {
    setBusy("convert");
    try {
      const r = await api("post", `/leads/${leadId}/convert`, { company: lead.company || "" });
      const count = r.data?.converted_lead_orders_count || 0;
      if (count > 0) {
        toast.success(`${lead.company_name} converted to client! ${count} order(s) moved to Orders section.`);
      } else {
        toast.success(r.data?.client ? `${lead.company_name} converted to client` : "Converted to client");
      }
      await loadLead();
      await loadLeadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not convert to client");
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async () => {
    setBusy("delete");
    try {
      await api("delete", `/leads/${leadId}`);
      toast.success(`${lead.company_name} deleted successfully`);
      if (onBack) onBack();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Could not delete lead";
      toast.error(errorMsg);
      setBusy("");
      setShowDeleteConfirm(false);
    }
  };

  const logActivity = async (e) => {
    e.preventDefault();
    if (!activity.summary.trim()) return;
    setBusy("activity");
    try {
      await api("post", `/leads/${leadId}/activities`, activity);
      setActivity({ type: "call", summary: "", outcome: "" });
      toast.success("Activity logged");
      await loadLead();
    } catch (err) {
      toast.error("Could not log activity");
    } finally {
      setBusy("");
    }
  };

  const saveFollowUp = async (e) => {
    e.preventDefault();
    if (!followUp.next_follow_up) {
      toast.error("Please select a follow-up date");
      return;
    }
    setBusy("followup");
    try {
      await api("patch", `/leads/${leadId}`, {
        next_follow_up: followUp.next_follow_up,
        follow_up_notes: (followUp.notes || "").trim(),
      });
      toast.success("Follow-up scheduled");
      setFollowUp({ next_follow_up: "", notes: "" });
      setShowFollowUpForm(false);
      await loadLead();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save follow-up");
    } finally {
      setBusy("");
    }
  };

  const clearFollowUp = async () => {
    setBusy("followup");
    try {
      await api("patch", `/leads/${leadId}`, {
        next_follow_up: "",
        follow_up_notes: "",
      });
      toast.success("Follow-up schedule cleared");
      setFollowUp({ next_follow_up: "", notes: "" });
      setShowFollowUpForm(false);
      await loadLead();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear follow-up");
    } finally {
      setBusy("");
    }
  };

  // Group requests into active vs completed
  const activeRequests = useMemo(
    () => priceRequests.filter((r) => r.status !== "Completed"),
    [priceRequests]
  );
  const completedRequests = useMemo(
    () => priceRequests.filter((r) => r.status === "Completed"),
    [priceRequests]
  );

  if (!lead) {
    return (
      <div className="content">
        <div className="loading-screen" style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
          Loading lead details…
        </div>
      </div>
    );
  }

  return (
    <div
      className="content detail-content"
      style={{
        maxWidth: 1240,
        margin: "0 auto",
        padding: "20px 24px 60px",
      }}
      data-testid="lead-detail-page"
    >
      {/* 1. Back to Leads Button (Clean dark navy button matching CRM detail pages) */}
      <div style={{ marginBottom: 16 }}>
        <button
          className="back-btn"
          data-testid="lead-detail-back-button"
          onClick={onBack}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            backgroundColor: "#0f172a",
            color: "#ffffff",
            border: "1px solid #0f172a",
            padding: "7px 16px",
            borderRadius: 6,
            fontSize: "12.5px",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#1e293b";
            e.currentTarget.style.borderColor = "#334155";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#0f172a";
            e.currentTarget.style.borderColor = "#0f172a";
          }}
        >
          <ArrowLeft size={14} style={{ strokeWidth: 2.2 }} /> Back to Leads
        </button>
      </div>

      {/* 2. Header: Lead Meta, Title, Contact Info & Action Buttons */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "20px 24px",
          marginBottom: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 280 }}>
          {/* Badge Strip */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#2563eb",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                padding: "2px 8px",
                borderRadius: 4,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              LEAD · {lead.lead_id}
            </span>
            {lead.converted_client_id && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#15803d",
                  background: "#dcfce7",
                  border: "1px solid #bbf7d0",
                  padding: "2px 8px",
                  borderRadius: 4,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Check size={12} /> Converted Client
              </span>
            )}
          </div>

          {/* Lead Name */}
          <h1
            style={{
              margin: "6px 0 6px",
              fontSize: 24,
              fontWeight: 700,
              color: "#0f172a",
              letterSpacing: "-0.4px",
            }}
          >
            {lead.company_name}
          </h1>

          {/* Contact Subtitle Line */}
          <div
            style={{
              fontSize: 13,
              color: "#475569",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              marginTop: 4,
            }}
          >
            {lead.contact_person && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#1e293b", fontWeight: 600 }}>
                <User size={14} style={{ color: "#64748b" }} /> {lead.contact_person}
                {lead.job_title && <span style={{ fontWeight: 400, color: "#64748b" }}>({lead.job_title})</span>}
              </span>
            )}
            {lead.email && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Mail size={13} style={{ color: "#64748b" }} /> {lead.email}
              </span>
            )}
            {canViewClientPhone(user) && lead.phone && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Phone size={13} style={{ color: "#64748b" }} /> {lead.phone}
              </span>
            )}
          </div>
        </div>

        {/* Top Right Actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", flexWrap: "wrap" }}>
          {/* Add Order Dropdown */}
          {canEdit && !lead.converted_client_id && (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="primary-btn"
                onClick={() => setShowAddOrderMenu((prev) => !prev)}
                data-testid="lead-add-order-dropdown-btn"
                style={{
                  height: 36,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "0 14px",
                  borderRadius: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                  border: "none",
                  color: "#ffffff",
                  boxShadow: "0 2px 6px rgba(37, 99, 235, 0.25)",
                  cursor: "pointer",
                }}
              >
                <Package size={15} /> Add Order <ChevronDown size={14} />
              </button>

              {showAddOrderMenu && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: 6,
                    background: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: 10,
                    boxShadow: "0 12px 30px -4px rgba(15, 23, 42, 0.18)",
                    padding: "6px",
                    minWidth: 200,
                    zIndex: 100,
                  }}
                  onClick={() => setShowAddOrderMenu(false)}
                >
                  <button
                    type="button"
                    onClick={() => setShowAddSingleOrder(true)}
                    data-testid="lead-add-single-order-menu-item"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      background: "none",
                      border: "none",
                      borderRadius: 6,
                      textAlign: "left",
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: "#0f172a",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                  >
                    <Plus size={14} style={{ color: "#2563eb" }} /> Add Single Order
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowUploadBulkOrders(true)}
                    data-testid="lead-upload-bulk-orders-menu-item"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      background: "none",
                      border: "none",
                      borderRadius: 6,
                      textAlign: "left",
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: "#0f172a",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                  >
                    <FileSpreadsheet size={14} style={{ color: "#16a34a" }} /> Upload Multiple Orders
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Request Price Button */}
          {canEdit && (
            <button
              type="button"
              className="outline-btn"
              onClick={handleOpenGeneralPriceModal}
              data-testid="lead-request-price-btn"
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 14px",
                borderRadius: 8,
                border: "1.5px solid #cbd5e1",
                background: selectedLeadOrderIds.length > 0 ? "#eff6ff" : "#ffffff",
                color: selectedLeadOrderIds.length > 0 ? "#1d4ed8" : "#334155",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
              }}
            >
              <ShoppingCart size={15} style={{ color: selectedLeadOrderIds.length > 0 ? "#2563eb" : "#64748b" }} />{" "}
              {selectedLeadOrderIds.length > 0
                ? `Request Price (${selectedLeadOrderIds.length})`
                : "Request Price"}
            </button>
          )}

          {/* Edit Button */}
          {canEdit && (
            <button
              type="button"
              className="outline-btn"
              onClick={() => setShowEditModal(true)}
              title="Edit Lead Details"
              data-testid="lead-detail-edit-button"
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 12px",
                borderRadius: 8,
                border: "1.5px solid #cbd5e1",
                background: "#ffffff",
                color: "#334155",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                cursor: "pointer",
              }}
            >
              <Edit2 size={14} style={{ color: "#64748b" }} /> Edit
            </button>
          )}

          {/* Delete Button */}
          {canDelete && !lead.converted_client_id && (
            <button
              type="button"
              className="outline-btn"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete Lead"
              data-testid="lead-detail-delete-button"
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 12px",
                borderRadius: 8,
                border: "1.5px solid #fecaca",
                background: "#fff5f5",
                color: "#dc2626",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fee2e2";
                e.currentTarget.style.borderColor = "#fca5a5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fff5f5";
                e.currentTarget.style.borderColor = "#fecaca";
              }}
            >
              <Trash2 size={14} style={{ color: "#dc2626" }} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* 3. Move Status Stepper Section */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "16px 20px",
          marginBottom: 20,
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        }}
        data-testid="lead-move-status-panel"
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#64748b",
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          MOVE STATUS
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
          data-testid="lead-status-chips"
        >
          {STATUSES.map((s) => {
            const isCurrent = s === lead.status;
            const pillStyle = STATUS_PILL_STYLES[s] || STATUS_PILL_STYLES.New;

            return (
              <button
                key={s}
                type="button"
                disabled={busy === "status" || isCurrent || !canEdit}
                onClick={() => changeStatus(s)}
                data-testid={`lead-status-${testid(s)}`}
                style={{
                  padding: "7px 16px",
                  borderRadius: 20,
                  fontSize: 12.5,
                  fontWeight: isCurrent ? 700 : 500,
                  cursor: isCurrent || !canEdit ? "default" : "pointer",
                  transition: "all 0.15s ease",
                  border: isCurrent
                    ? `1.5px solid ${pillStyle.activeBorder}`
                    : "1px solid #e2e8f0",
                  background: isCurrent ? pillStyle.activeBg : "#ffffff",
                  color: isCurrent ? pillStyle.activeText : "#334155",
                  boxShadow: isCurrent ? "0 2px 4px rgba(0,0,0,0.06)" : "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {isCurrent && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: pillStyle.dot,
                      display: "inline-block",
                    }}
                  />
                )}
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {/* 4. Main 2-Column Grid (Lead Information + Activity History) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.45fr 1fr",
          gap: 20,
          alignItems: "start",
          marginBottom: 20,
        }}
      >
        {/* LEFT COLUMN: Lead Information */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Lead Information Card */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "18px 22px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            }}
            data-testid="lead-info-card"
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                marginBottom: 14,
              }}
            >
              LEAD INFORMATION
            </div>

            {/* Information Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "14px 20px",
                fontSize: 13,
              }}
            >
              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 11.5, fontWeight: 500, marginBottom: 2 }}>
                  Company / Organization
                </span>
                <b style={{ color: "#0f172a", fontSize: 13.5 }}>{lead.company || "—"}</b>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 11.5, fontWeight: 500, marginBottom: 2 }}>
                  Source
                </span>
                <b style={{ color: "#0f172a", fontSize: 13.5 }}>{lead.source || "—"}</b>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 11.5, fontWeight: 500, marginBottom: 2 }}>
                  Estimated Value
                </span>
                <b style={{ color: "#15803d", fontSize: 14, fontWeight: 700 }}>
                  {money(lead.estimated_value || 0)}
                </b>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 11.5, fontWeight: 500, marginBottom: 2 }}>
                  Owner / Assigned
                </span>
                <b style={{ color: "#0f172a", fontSize: 13.5 }}>{lead.assigned_to || "Unassigned"}</b>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 11.5, fontWeight: 500, marginBottom: 2 }}>
                  Product Interest
                </span>
                <b style={{ color: "#0f172a", fontSize: 13.5 }}>{lead.product_interest || "—"}</b>
              </div>
            </div>

            {/* Follow-up Section */}
            <div
              style={{
                marginTop: 16,
                padding: "12px 14px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                fontSize: 12.5,
              }}
              data-testid="lead-followup-container"
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 220 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Calendar size={15} style={{ color: lead.status === "Lost" ? "#94a3b8" : "#2563eb", flexShrink: 0 }} />
                    <span style={{ color: "#64748b" }}>Scheduled Follow-Up:</span>
                    <strong style={{ color: lead.next_follow_up ? "#0f172a" : "#94a3b8" }} data-testid="lead-scheduled-followup-date">
                      {lead.next_follow_up
                        ? formatDate(lead.next_follow_up)
                        : lead.status === "Lost"
                        ? (lead.previous_follow_up ? `Deactivated (Previous: ${formatDate(lead.previous_follow_up)})` : "None scheduled (Lead Lost)")
                        : "None scheduled"}
                    </strong>
                  </div>

                  {lead.next_follow_up && lead.follow_up_notes && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        paddingLeft: 23,
                      }}
                      data-testid="lead-scheduled-followup-reminder"
                    >
                      <span style={{ color: "#64748b", fontWeight: 600, flexShrink: 0 }}>Reminder:</span>
                      <span
                        style={{
                          color: "#1e293b",
                          fontWeight: 500,
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.45,
                        }}
                      >
                        {lead.follow_up_notes}
                      </span>
                    </div>
                  )}
                </div>

                {canEdit && !showFollowUpForm && lead.status !== "Lost" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button
                      type="button"
                      className="outline-btn compact"
                      onClick={() => {
                        setFollowUp({
                          next_follow_up: lead.next_follow_up || "",
                          notes: lead.follow_up_notes || "",
                        });
                        setShowFollowUpForm(true);
                      }}
                      data-testid="lead-add-followup-button"
                      style={{
                        height: 28,
                        fontSize: 11.5,
                        fontWeight: 600,
                        padding: "0 10px",
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        background: "#ffffff",
                        color: "#334155",
                        cursor: "pointer",
                      }}
                    >
                      {lead.next_follow_up ? "Edit Follow-Up" : "Set Follow-Up"}
                    </button>
                    {lead.next_follow_up && (
                      <button
                        type="button"
                        className="outline-btn compact"
                        onClick={clearFollowUp}
                        disabled={busy === "followup"}
                        data-testid="lead-clear-followup-button"
                        title="Clear scheduled follow-up"
                        style={{
                          height: 28,
                          fontSize: 11.5,
                          fontWeight: 600,
                          padding: "0 8px",
                          borderRadius: 6,
                          border: "1px solid #fecaca",
                          background: "#fff1f2",
                          color: "#be123c",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Inline Follow-up Form */}
            {showFollowUpForm && (
              <form
                onSubmit={saveFollowUp}
                data-testid="lead-followup-form"
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 8,
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 8, alignItems: "center" }}>
                  <input
                    type="date"
                    value={followUp.next_follow_up}
                    min={getTodayDateString()}
                    onChange={(e) => setFollowUp({ ...followUp, next_follow_up: e.target.value })}
                    required
                    style={{ height: 34, fontSize: 12.5, border: "1.5px solid #cbd5e1", borderRadius: 6, padding: "0 8px" }}
                    className="form-control"
                    data-testid="lead-followup-date-input"
                  />
                  <input
                    type="text"
                    placeholder="Brief reminder / agenda..."
                    value={followUp.notes}
                    onChange={(e) => setFollowUp({ ...followUp, notes: e.target.value })}
                    style={{ height: 34, fontSize: 12.5, border: "1.5px solid #cbd5e1", borderRadius: 6, padding: "0 10px" }}
                    className="form-control"
                    data-testid="lead-followup-notes-input"
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="submit"
                      className="primary-btn compact"
                      disabled={busy === "followup"}
                      data-testid="lead-followup-save-button"
                      style={{
                        height: 34,
                        fontSize: 12,
                        padding: "0 12px",
                        background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                      }}
                    >
                      Save
                    </button>
                    {lead.next_follow_up && (
                      <button
                        type="button"
                        className="outline-btn compact"
                        onClick={clearFollowUp}
                        disabled={busy === "followup"}
                        data-testid="lead-followup-clear-inline-button"
                        style={{ height: 34, fontSize: 12, padding: "0 8px", borderColor: "#fecaca", color: "#be123c", background: "#fff1f2" }}
                        title="Clear follow-up"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      className="outline-btn compact"
                      onClick={() => setShowFollowUpForm(false)}
                      data-testid="lead-followup-cancel-button"
                      style={{ height: 34, fontSize: 12, padding: "0 8px" }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Notes */}
            {lead.notes && (
              <div
                style={{
                  marginTop: 14,
                  padding: "10px 14px",
                  background: "#f8fafc",
                  borderLeft: "3px solid #cbd5e1",
                  borderRadius: 4,
                  fontSize: 12.5,
                  color: "#334155",
                }}
              >
                <span style={{ color: "#64748b", fontWeight: 700, display: "block", fontSize: 11, textTransform: "uppercase", marginBottom: 3 }}>
                  Notes
                </span>
                <p style={{ margin: 0, lineHeight: 1.5 }}>{lead.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Activity Logging & Timeline */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "18px 20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            }}
            data-testid="lead-activity-panel"
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}
              >
                ACTIVITY & HISTORY
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#475569",
                  background: "#f1f5f9",
                  padding: "2px 8px",
                  borderRadius: 10,
                }}
              >
                {(lead.activities || []).length} logs
              </span>
            </div>

            {/* Activity Log Form with generous width */}
            {canEdit && (
              <form
                onSubmit={logActivity}
                data-testid="lead-activity-form"
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "12px",
                  marginBottom: 14,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
                  <select
                    className="form-control"
                    value={activity.type}
                    onChange={(e) => setActivity({ ...activity, type: e.target.value })}
                    data-testid="activity-type-select"
                    style={{
                      width: 90,
                      height: 36,
                      fontSize: 12.5,
                      fontWeight: 500,
                      padding: "0 8px",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: 6,
                      backgroundColor: "#ffffff",
                      flexShrink: 0,
                    }}
                  >
                    <option value="call">Call</option>
                    <option value="email">Email</option>
                    <option value="meeting">Meeting</option>
                    <option value="note">Note</option>
                  </select>

                  <input
                    className="form-control"
                    placeholder="Brief activity note..."
                    value={activity.summary}
                    onChange={(e) => setActivity({ ...activity, summary: e.target.value })}
                    required
                    data-testid="activity-summary-input"
                    style={{
                      flex: 1,
                      minWidth: 140,
                      height: 36,
                      fontSize: 12.5,
                      border: "1.5px solid #cbd5e1",
                      borderRadius: 6,
                      padding: "0 10px",
                    }}
                  />

                  <button
                    type="submit"
                    className="primary-btn compact"
                    disabled={busy === "activity"}
                    data-testid="activity-log-button"
                    style={{
                      height: 36,
                      fontSize: 12.5,
                      fontWeight: 600,
                      padding: "0 14px",
                      borderRadius: 6,
                      background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                      flexShrink: 0,
                    }}
                  >
                    {busy === "activity" ? "Logging..." : "Log"}
                  </button>
                </div>
              </form>
            )}

            {/* Activity Timeline List */}
            <div
              style={{
                maxHeight: 280,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
              data-testid="lead-activity-list"
            >
              {(lead.activities || []).slice().reverse().map((a, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: "#f8fafc",
                    border: "1px solid #f1f5f9",
                    fontSize: 12.5,
                  }}
                  data-testid={`activity-row-${i}`}
                >
                  <ActivityIcon type={a.type} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "#0f172a", lineHeight: 1.35 }}>
                      {a.summary}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
                      {a.by} · {new Date(a.at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                      {a.outcome && ` — ${a.outcome}`}
                    </div>
                  </div>
                </div>
              ))}
              {(lead.activities || []).length === 0 && (
                <div
                  style={{
                    padding: 24,
                    textAlign: "center",
                    color: "#94a3b8",
                    fontSize: 12.5,
                    background: "#f8fafc",
                    borderRadius: 6,
                  }}
                >
                  No activities logged yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 5. Lead Orders Section (Full width, modern table with Price column) */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "18px 22px",
          marginBottom: 20,
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        }}
        data-testid="lead-orders-section"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "#eff6ff",
                color: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Package size={16} />
            </div>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Lead Orders
            </span>
            {leadOrders.length > 0 && (
              <span
                style={{
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 10,
                  border: "1px solid #bfdbfe",
                }}
              >
                {leadOrders.length} draft {leadOrders.length === 1 ? "order" : "orders"}
              </span>
            )}
          </div>

          {canEdit && !lead.converted_client_id && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {selectedLeadOrderIds.length > 0 && (
                <button
                  type="button"
                  className="primary-btn compact"
                  onClick={handleRequestPriceForSelected}
                  data-testid="lead-panel-request-price-selected-btn"
                  style={{
                    height: 30,
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "0 12px",
                    borderRadius: 6,
                    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    borderColor: "#1d4ed8",
                    color: "#ffffff",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    boxShadow: "0 1px 3px rgba(37,99,235,0.25)",
                    cursor: "pointer",
                  }}
                >
                  <ShoppingCart size={13} /> Request Price for Selected ({selectedLeadOrderIds.length})
                </button>
              )}
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setShowAddSingleOrder(true)}
                data-testid="lead-panel-add-single-order-btn"
                style={{
                  height: 30,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#334155",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                }}
              >
                <Plus size={13} style={{ color: "#2563eb" }} /> Add Single Order
              </button>
              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setShowUploadBulkOrders(true)}
                data-testid="lead-panel-upload-bulk-orders-btn"
                style={{
                  height: 30,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#334155",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                }}
              >
                <FileSpreadsheet size={13} style={{ color: "#16a34a" }} /> Upload Multiple Orders
              </button>
            </div>
          )}
        </div>

        {/* Selected Products Toolbar */}
        {canEdit && selectedLeadOrderIds.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
              background: "#eff6ff",
              border: "1.5px solid #bfdbfe",
              borderRadius: 8,
              marginBottom: 12,
              boxShadow: "0 2px 4px rgba(37, 99, 235, 0.08)",
              flexWrap: "wrap",
              gap: 10,
            }}
            data-testid="lead-orders-bulk-actions-bar"
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#1d4ed8" }}>
              <span
                style={{
                  background: "#2563eb",
                  color: "#ffffff",
                  minWidth: 22,
                  height: 22,
                  padding: "0 6px",
                  borderRadius: 11,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11.5,
                  fontWeight: 700,
                }}
              >
                {selectedLeadOrderIds.length}
              </span>
              <span>
                {selectedLeadOrderIds.length} product{selectedLeadOrderIds.length === 1 ? "" : "s"} selected
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="primary-btn compact"
                onClick={handleRequestPriceForSelected}
                data-testid="request-price-selected-btn"
                style={{
                  fontSize: 12.5,
                  padding: "6px 14px",
                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                  borderColor: "#1d4ed8",
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 600,
                  boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)",
                  cursor: "pointer",
                }}
              >
                <ShoppingCart size={14} /> Request Price for Selected ({selectedLeadOrderIds.length})
              </button>

              <button
                type="button"
                className="outline-btn compact"
                onClick={() => setSelectedLeadOrderIds([])}
                data-testid="clear-selected-lead-orders-btn"
                style={{ fontSize: 12, padding: "5px 10px", borderRadius: 6, color: "#64748b" }}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {loadingOrders ? (
          <div style={{ padding: 24, fontSize: 13, color: "#64748b", textAlign: "center" }}>
            Loading lead orders...
          </div>
        ) : leadOrders.length === 0 ? (
          <div
            style={{
              padding: "24px 16px",
              background: "#f8fafc",
              border: "1px dashed #cbd5e1",
              borderRadius: 8,
              textAlign: "center",
              fontSize: 13,
              color: "#64748b",
            }}
          >
            No draft orders attached to this lead yet. Click <b>"+ Add Single Order"</b> or <b>"Upload Multiple Orders"</b> to create items.
          </div>
        ) : (
          <div>
            <div
              style={{
                overflowX: "auto",
                border: "1.5px solid #e2e8f0",
                borderRadius: 8,
                marginBottom: 10,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "1.5px solid #e2e8f0",
                      textAlign: "left",
                    }}
                  >
                    {/* Checkbox: Select All */}
                    <th
                      style={{ padding: "10px 12px", width: 44, textAlign: "center", cursor: "pointer" }}
                      onClick={toggleSelectAllOrders}
                    >
                      <input
                        type="checkbox"
                        checked={allOrdersSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = isOrdersIndeterminate;
                        }}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllOrders();
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Select all products"
                        style={{ cursor: "pointer", width: 16, height: 16, accentColor: "#2563eb" }}
                        data-testid="select-all-lead-orders-checkbox"
                      />
                    </th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 115, whiteSpace: "nowrap" }}>
                      ORDER ID
                    </th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, minWidth: 150 }}>
                      DESCRIPTION
                    </th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 140 }}>
                      PRODUCT
                    </th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 90 }}>
                      QUANTITY
                    </th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 75 }}>
                      UNIT
                    </th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 145, whiteSpace: "nowrap" }}>
                      PRICE
                    </th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 155, whiteSpace: "nowrap" }}>
                      STATUS
                    </th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 155, textAlign: "right", whiteSpace: "nowrap" }}>
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leadOrders.map((lo) => {
                    const isSelected = selectedLeadOrderIds.includes(lo.order_id);
                    return (
                      <tr
                        key={lo.order_id}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: isSelected ? "#f8faff" : "#ffffff",
                          transition: "background 0.1s ease",
                        }}
                        data-testid={`lead-order-row-${lo.order_id}`}
                      >
                        {/* Checkbox Cell */}
                        <td
                          style={{ padding: "10px 12px", textAlign: "center", cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelectOrder(lo.order_id);
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSelectOrder(lo.order_id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select product ${lo.order_id}`}
                            style={{ cursor: "pointer", width: 16, height: 16, accentColor: "#2563eb" }}
                            data-testid={`select-lead-order-checkbox-${lo.order_id}`}
                          />
                        </td>
                        <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 12, color: "#0f172a", fontWeight: 700, whiteSpace: "nowrap" }}>
                          {lo.order_id}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#334155", fontSize: 12.5, lineHeight: 1.4, minWidth: 150 }}>
                          {lo.description ? (
                            <span>{lo.description}</span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px", fontWeight: 600, color: "#0f172a", width: 140 }}>
                          <div>{lo.product || "—"}</div>
                          {lo.notes && (
                            <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", marginTop: 2, fontWeight: 400 }}>
                              {lo.notes}
                            </div>
                          )}
                          {lo.pricing_notes && (
                            <div style={{ fontSize: 11, color: "#2563eb", marginTop: 2, fontWeight: 500 }}>
                              💬 {lo.pricing_notes}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px", fontWeight: 700, color: "#0f172a", width: 90 }}>
                          {(lo.quantity || 1).toLocaleString()}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#64748b", width: 75 }}>
                          {lo.unit || "pcs"}
                        </td>
                        <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }} data-testid={`lead-order-price-cell-${lo.order_id}`}>
                          {lo.unit_price && lo.unit_price > 0 ? (
                            <div>
                              <div style={{ fontWeight: 700, color: "#15803d", fontSize: 12.5 }}>
                                {money(lo.unit_price)}
                                <span style={{ fontSize: 11, fontWeight: 500, color: "#64748b" }}> / {lo.unit || "pcs"}</span>
                              </div>
                              {((lo.total_value && lo.total_value > 0) || (lo.value && lo.value > 0)) && (lo.quantity || 1) > 1 && (
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                  Total: <b style={{ color: "#1e293b" }}>{money(lo.value || lo.total_value)}</b>
                                </div>
                              )}
                              {lo.procurement_price_received && (
                                <div style={{ marginTop: 2 }}>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: "#15803d",
                                      background: "#dcfce7",
                                      border: "1px solid #bbf7d0",
                                      padding: "1px 6px",
                                      borderRadius: 4,
                                      display: "inline-block",
                                    }}
                                  >
                                    ✓ Procurement Price
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (lo.value && lo.value > 0) ? (
                            <div>
                              <div style={{ fontWeight: 700, color: "#15803d", fontSize: 12.5 }}>
                                {money(lo.value)}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <span style={{ fontSize: 11.5, color: "#94a3b8", fontStyle: "italic" }}>
                                Price Pending
                              </span>
                              {lo.procurement_status === "Requested" && (
                                <div style={{ marginTop: 2 }}>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      color: "#92400e",
                                      background: "#fef3c7",
                                      border: "1px solid #fde68a",
                                      padding: "1px 6px",
                                      borderRadius: 4,
                                      display: "inline-block",
                                    }}
                                  >
                                    Awaiting Procurement
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              padding: "4px 10px",
                              borderRadius: 6,
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              border: "1px solid #bfdbfe",
                              display: "inline-block",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Draft / Lead Order
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "flex-end", whiteSpace: "nowrap" }}>
                            {canEdit && !lead.converted_client_id && (
                              <button
                                type="button"
                                className="outline-btn compact"
                                onClick={() => setPriceOrder(lo)}
                                title={lo.unit_price > 0 ? "Update Price" : "Add Price"}
                                data-testid={`lead-order-price-btn-${lo.order_id}`}
                                style={{
                                  height: 28,
                                  fontSize: 11.5,
                                  padding: "0 10px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  color: lo.unit_price > 0 ? "#2563eb" : "#15803d",
                                  borderColor: lo.unit_price > 0 ? "#bfdbfe" : "#bbf7d0",
                                  background: lo.unit_price > 0 ? "#eff6ff" : "#f0fdf4",
                                  fontWeight: 600,
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <IndianRupee size={12} style={{ flexShrink: 0 }} />
                                <span>{lo.unit_price > 0 ? "Update Price" : "Add Price"}</span>
                              </button>
                            )}
                            {canEdit && !lead.converted_client_id && (
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => handleDeleteLeadOrder(lo.order_id)}
                                title="Remove Order"
                                data-testid={`delete-lead-order-btn-${lo.order_id}`}
                                style={{
                                  height: 28,
                                  width: 28,
                                  padding: 0,
                                  color: "#dc2626",
                                  background: "#fef2f2",
                                  border: "1px solid #fecaca",
                                  borderRadius: 6,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
              <Info size={14} style={{ color: "#2563eb", flexShrink: 0 }} />
              <span>These draft orders will automatically move to the main Orders section once converted to a Client.</span>
            </div>
          </div>
        )}
      </div>

      {/* 6. Procurement & Price Requests Section */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "18px 22px",
          marginBottom: 20,
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        }}
        data-testid="lead-procurement-section"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "#f1f5f9",
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCart size={15} />
            </div>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Procurement & Price Requests
            </span>
            {activeRequests.length > 0 && (
              <span
                style={{
                  background: "#f1f5f9",
                  color: "#0f172a",
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 10,
                  border: "1px solid #e2e8f0",
                }}
              >
                {activeRequests.length} active
              </span>
            )}
          </div>
          {canEdit && (
            <button
              type="button"
              className="outline-btn compact"
              onClick={handleOpenGeneralPriceModal}
              data-testid="lead-add-price-request-btn"
              style={{
                height: 30,
                fontSize: 12,
                fontWeight: 600,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#334155",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                cursor: "pointer",
              }}
            >
              <Plus size={13} style={{ color: "#2563eb" }} /> Request Price
            </button>
          )}
        </div>

        {loadingProc ? (
          <div style={{ padding: 24, fontSize: 13, color: "#64748b", textAlign: "center" }}>
            Loading price requests...
          </div>
        ) : priceRequests.length === 0 ? (
          <div
            style={{
              padding: "24px 16px",
              background: "#f8fafc",
              border: "1px dashed #cbd5e1",
              borderRadius: 8,
              textAlign: "center",
              fontSize: 13,
              color: "#64748b",
            }}
          >
            No price requests for this lead yet. Need vendor pricing? Click <b>"Request Price"</b>.
          </div>
        ) : (
          <div>
            {/* Active Requests Table */}
            {activeRequests.length > 0 ? (
              <div
                style={{
                  overflowX: "auto",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 8,
                  marginBottom: 10,
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", textAlign: "left" }}>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5 }}>Product</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 90 }}>Qty</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 80 }}>Priority</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 110 }}>Status</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5 }}>Rate / Price</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, color: "#475569", fontSize: 11.5, width: 50, textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRequests.flatMap((pr) => {
                      const itemsList = pr.items && pr.items.length > 0 ? pr.items : [{
                        item_id: "itm_1",
                        product_name: pr.product_name,
                        quantity: pr.quantity,
                        unit: pr.unit,
                        priority: pr.priority,
                        status: pr.status,
                        price: pr.price,
                        currency: pr.currency,
                        pricing_unit: pr.pricing_unit,
                        total_estimated_price: pr.total_estimated_price,
                        vendor_name: pr.vendor_name,
                        procurement_notes: pr.procurement_notes,
                        additional_notes: pr.additional_notes,
                      }];

                      return itemsList.map((item, idx) => {
                        const isItemPriced = item.status === "Price Received" || (item.price !== null && item.price !== undefined);
                        const itemStatus = isItemPriced ? "Price Received" : "Pending";
                        const statusStyle = PROC_STATUS_BADGES[itemStatus] || PROC_STATUS_BADGES.Pending;
                        const priorityStyle = PROC_PRIORITY_BADGES[item.priority || pr.priority] || PROC_PRIORITY_BADGES.Medium;
                        const rowKey = `${pr.request_id}-${item.item_id || idx}`;

                        return (
                          <tr
                            key={rowKey}
                            style={{ borderBottom: "1px solid #f1f5f9", background: "#ffffff" }}
                            data-testid={`lead-price-request-row-${pr.request_id}-${item.item_id || idx}`}
                          >
                            {/* Product */}
                            <td style={{ padding: "10px 12px", fontWeight: 600, color: "#0f172a" }}>
                              <div>{item.product_name}</div>
                              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 400, marginTop: 2 }}>
                                <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#0f172a" }}>{pr.request_id}</span>
                                {itemsList.length > 1 && ` (Item ${idx + 1}/${itemsList.length})`} · {pr.assigned_to_name || "Procurement Team"}
                              </div>
                              {item.additional_notes && (
                                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2, fontStyle: "italic" }}>
                                  {item.additional_notes}
                                </div>
                              )}
                            </td>

                            {/* Quantity */}
                            <td style={{ padding: "10px 12px", color: "#0f172a", fontWeight: 600, whiteSpace: "nowrap" }}>
                              {(item.quantity || 1).toLocaleString()} {item.unit || "units"}
                            </td>

                            {/* Priority */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: priorityStyle.bg,
                                  color: priorityStyle.text,
                                }}
                              >
                                {item.priority || pr.priority || "Medium"}
                              </span>
                            </td>

                            {/* Status */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: "3px 7px",
                                  borderRadius: 4,
                                  background: statusStyle.bg,
                                  color: statusStyle.text,
                                  border: `1px solid ${statusStyle.border || "#e2e8f0"}`,
                                }}
                              >
                                {itemStatus}
                              </span>
                            </td>

                            {/* Price */}
                            <td style={{ padding: "10px 12px" }}>
                              {isItemPriced ? (
                                <div>
                                  <strong style={{ color: "#15803d", fontSize: 13 }}>
                                    {formatCurrency(item.price, item.currency || pr.currency)}
                                    <small style={{ fontWeight: 500, color: "#64748b", fontSize: 11 }}>/{item.pricing_unit || item.unit || "unit"}</small>
                                  </strong>
                                  {item.total_estimated_price > 0 && (
                                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                      Total: <b style={{ color: "#1e293b" }}>{formatCurrency(item.total_estimated_price, item.currency || pr.currency)}</b>
                                    </div>
                                  )}
                                  {item.vendor_name && (
                                    <div style={{ fontSize: 11, color: "#0369a1", marginTop: 2 }} title={item.vendor_name}>
                                      Supplier: {item.vendor_name}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: "#94a3b8", fontSize: 11.5, fontStyle: "italic" }}>Awaiting rate</span>
                              )}
                            </td>

                            {/* Action: Details Modal */}
                            <td style={{ padding: "10px 12px", textAlign: "right" }}>
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setSelectedReqDetail(pr)}
                                title="View Details & Notes"
                                style={{ height: 26, width: 26, padding: 0, borderRadius: 5, color: "#0f172a" }}
                              >
                                <ExternalLink size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: 6, fontSize: 12.5, color: "#64748b", marginBottom: 10 }}>
                No active or pending price requests.
              </div>
            )}

            {/* Completed Requests Toggle */}
            {completedRequests.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowCompletedProc((prev) => !prev)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "4px 0",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#475569",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  {showCompletedProc ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  Completed Price Requests ({completedRequests.length})
                </button>

                {showCompletedProc && (
                  <div style={{ marginTop: 8, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <tbody>
                        {completedRequests.map((pr) => (
                          <tr key={pr.request_id} style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                            <td style={{ padding: "8px 12px", color: "#0f172a", fontWeight: 600 }}>
                              {pr.product_name} ({pr.quantity} {pr.unit})
                            </td>
                            <td style={{ padding: "8px 12px", color: "#64748b", fontFamily: "monospace" }}>
                              {pr.request_id}
                            </td>
                            <td style={{ padding: "8px 12px", color: "#15803d", fontWeight: 700 }}>
                              {formatCurrency(pr.price, pr.currency)}
                            </td>
                            <td style={{ padding: "8px 12px", color: "#64748b" }}>
                              {pr.vendor_name || "—"}
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "right" }}>
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setSelectedReqDetail(pr)}
                                title="View Details"
                                style={{ height: 22, width: 22, padding: 0 }}
                              >
                                <ExternalLink size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 7. Bottom Actions Docked Bar */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "16px 22px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
        data-testid="lead-actions-panel"
      >
        <div>
          {lead.converted_client_id ? (
            <div style={{ fontSize: 13, color: "#15803d", display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
              <CheckCircle2 size={16} />
              <span>
                Converted to Client: <b style={{ color: "#14532d" }}>{lead.client?.company_name || lead.company_name}</b> ({lead.converted_client_id})
                {(lead.client?.company || lead.company) && (
                  <span
                    style={{
                      marginLeft: 8,
                      padding: "2px 8px",
                      background: "#dcfce7",
                      color: "#166534",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                    data-testid="lead-converted-company-badge"
                  >
                    {lead.client?.company || lead.company}
                  </span>
                )}
              </span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#475569" }}>
              Lead Status: <b style={{ color: "#0f172a" }}>{lead.status}</b>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {canEdit && !lead.converted_client_id && (
            <button
              type="button"
              className="primary-btn"
              disabled={busy === "convert"}
              onClick={handleConvert}
              data-testid="lead-convert-button"
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 16px",
                borderRadius: 8,
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                border: "none",
                color: "#ffffff",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 2px 6px rgba(15, 23, 42, 0.2)",
                cursor: busy === "convert" ? "not-allowed" : "pointer",
              }}
            >
              <UserPlus size={15} /> {busy === "convert" ? "Converting…" : "Convert to Client"}
            </button>
          )}

          {canEdit && !lead.converted_client_id && (
            <button
              type="button"
              className="outline-btn"
              disabled={busy === "delete"}
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="lead-delete-button"
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 14px",
                borderRadius: 8,
                color: "#dc2626",
                borderColor: "#fca5a5",
                background: "#ffffff",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                cursor: "pointer",
              }}
            >
              <Trash2 size={14} /> Delete Lead
            </button>
          )}
        </div>
      </div>

      {/* Modal: View Individual Price Request Details */}
      {selectedReqDetail && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10000, backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}
          data-testid="price-request-detail-modal"
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 540, width: "95%", padding: "24px 26px", borderRadius: 14, maxHeight: "85vh", overflowY: "auto" }}
          >
            <div className="modal-head" style={{ marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, color: "#0f172a", fontWeight: 700 }}>Price Request Details</h2>
                <div style={{ fontSize: 11.5, color: "#64748b", fontFamily: "monospace", marginTop: 2 }}>
                  {selectedReqDetail.request_id} · Overall Status: <b>{selectedReqDetail.status}</b>
                </div>
              </div>
              <button type="button" className="icon-btn" onClick={() => setSelectedReqDetail(null)}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b" }}>Assigned Specialist:</span>
                <span style={{ color: "#0f172a", fontWeight: 600 }}>{selectedReqDetail.assigned_to_name || "Procurement Team"}</span>
              </div>
              {selectedReqDetail.required_by && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#64748b" }}>Required By:</span>
                  <span style={{ color: "#0f172a" }}>{formatDate(selectedReqDetail.required_by)}</span>
                </div>
              )}

              {/* Products List inside Request */}
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", marginBottom: 6 }}>
                  Requested Products &amp; Pricing
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(selectedReqDetail.items && selectedReqDetail.items.length > 0 ? selectedReqDetail.items : [selectedReqDetail]).map((item, idx) => {
                    const hasItemPrice = item.price !== null && item.price !== undefined && item.status === "Price Received";
                    return (
                      <div
                        key={item.item_id || idx}
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: 8,
                          padding: "10px 12px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>
                            {idx + 1}. {item.product_name}
                          </span>
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 600,
                              padding: "2px 6px",
                              borderRadius: 4,
                              background: hasItemPrice ? "#dcfce7" : "#fef3c7",
                              color: hasItemPrice ? "#15803d" : "#92400e",
                            }}
                          >
                            {hasItemPrice ? "Price Received" : "Pending"}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          Qty: <b>{(item.quantity || 1).toLocaleString()} {item.unit || "units"}</b> · Priority: <b>{item.priority || "Medium"}</b>
                        </div>
                        {hasItemPrice ? (
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed #cbd5e1", fontSize: 12.5, color: "#166534" }}>
                            <div>
                              Rate: <b>{formatCurrency(item.price, item.currency || selectedReqDetail.currency)} /{item.pricing_unit || item.unit || "unit"}</b>
                              {item.total_estimated_price > 0 && (
                                <span style={{ marginLeft: 8, color: "#1e293b" }}>
                                  (Total: <b>{formatCurrency(item.total_estimated_price, item.currency || selectedReqDetail.currency)}</b>)
                                </span>
                              )}
                            </div>
                            {item.vendor_name && (
                              <div style={{ fontSize: 11, color: "#0369a1", marginTop: 2 }}>
                                Supplier: {item.vendor_name} {item.vendor_contact ? `(${item.vendor_contact})` : ""}
                              </div>
                            )}
                            {item.procurement_notes && (
                              <div style={{ fontSize: 11, color: "#065f46", marginTop: 2, fontStyle: "italic" }}>
                                Note: {item.procurement_notes}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ marginTop: 4, fontSize: 11.5, color: "#94a3b8", fontStyle: "italic" }}>
                            Price Pending from Procurement
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button
                type="button"
                className="primary-btn compact"
                onClick={() => setSelectedReqDetail(null)}
                style={{ height: 34, fontSize: 12.5, padding: "0 16px" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Request Price */}
      <RequestPriceModal
        isOpen={priceModalOpen}
        onClose={() => {
          setPriceModalOpen(false);
          setOrdersForPriceRequest([]);
        }}
        lead={lead}
        selectedOrders={ordersForPriceRequest}
        currentUser={user}
        onSuccess={() => {
          loadProcurementRequests();
          loadLeadOrders();
          loadLead();
          setSelectedLeadOrderIds([]);
          setOrdersForPriceRequest([]);
        }}
      />

      {/* Modal: Delete Confirmation */}
      {showDeleteConfirm && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10001 }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 460 }}
            data-testid="delete-lead-confirmation-modal"
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  background: "#fee2e2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Trash2 size={22} />
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: "0 0 6px 0", fontSize: 18, color: "var(--navy)" }}>
                  Delete Lead
                </h2>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)", lineHeight: 1.5 }}>
                  Are you sure you want to delete this lead?
                </p>
                <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}>
                  <b style={{ color: "var(--navy)" }}>{lead.company_name}</b> will be removed from your active leads list.
                </p>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={busy === "delete"}
                data-testid="cancel-delete-lead-button"
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleDelete}
                disabled={busy === "delete"}
                style={{ background: "var(--red)", borderColor: "var(--red)" }}
                data-testid="confirm-delete-lead-button"
              >
                {busy === "delete" ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Order Modal */}
      {showAddSingleOrder && (
        <AddLeadOrderModal
          lead={lead}
          isOpen={showAddSingleOrder}
          onClose={() => setShowAddSingleOrder(false)}
          onCreated={loadLeadOrders}
        />
      )}

      {/* Bulk Lead Order Upload Modal */}
      {showUploadBulkOrders && (
        <BulkLeadOrderUploadModal
          lead={lead}
          isOpen={showUploadBulkOrders}
          onClose={() => setShowUploadBulkOrders(false)}
          onCreated={loadLeadOrders}
        />
      )}

      {/* Update / Add Lead Order Price Modal */}
      {priceOrder && (
        <UpdateLeadOrderPriceModal
          lead={lead}
          order={priceOrder}
          isOpen={Boolean(priceOrder)}
          onClose={() => setPriceOrder(null)}
          onUpdated={loadLeadOrders}
        />
      )}

      {/* Edit Lead Modal */}
      {showEditModal && (
        <EditLeadModal
          leadId={leadId}
          onClose={(refreshed) => {
            setShowEditModal(false);
            if (refreshed) {
              loadLead();
            }
          }}
        />
      )}
    </div>
  );
}
